import type { BirthdayDto, CalendarEventDto, CalendarEventTaskDto, CalendarItemDto, MatchDto, TaskTemplateDto } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ApiError, api, buildQuery } from '../lib/api';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarMonth, dayKey } from '../components/CalendarMonth';
import { scopeQuery, useScope } from '../lib/scope';
import { useAuth } from '../lib/auth';
import { useUnsavedChanges } from '../lib/useUnsavedChanges';
import {
  EVENT_TYPE_LABELS,
  MATCH_STATUS_LABELS,
  formatShort,
  formatTime,
  fromDateInput,
  fromDateTimeLocal,
  toDateInput,
  toDateTimeLocal,
} from '../lib/format';
import { OrgUnitPicker } from '../components/OrgUnitPicker';
import { PersonPicker, PersonSelect } from '../components/PersonPicker';
import { PageHeader } from '../components/PageHeader';
import { TemplateDialog } from '../components/TemplateDialog';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Checkbox,
  cn,
  Dialog,
  EmptyState,
  ErrorBlock,
  Field,
  Input,
  LoadingBlock,
  Select,
  Tabs,
  Textarea,
  useToast,
} from '../components/ui';

const EVENT_TONES: Record<string, 'brand' | 'info' | 'success' | 'warning' | 'neutral'> = {
  MATCH: 'brand',
  TRAINING: 'info',
  MEETING: 'warning',
  EVENT: 'success',
  TASK_DEADLINE: 'neutral',
  SPONSOR: 'warning',
  OTHER: 'neutral',
};

const BIRTHDAY_KEY = 'ukinfo:kalender:geburtstage';

/** "Do, 24. September" - Jahr nur, wenn es nicht das laufende ist. */
function tagUeberschrift(value: string): string {
  const datum = new Date(value);
  const mitJahr = datum.getFullYear() !== new Date().getFullYear();
  return datum.toLocaleDateString('de-AT', { weekday: 'short', day: 'numeric', month: 'long', ...(mitJahr ? { year: 'numeric' } : {}) });
}

/** Linker Streifen je Art: gleiche Farben wie auf der persoenlichen Seite. */
function streifen(item: CalendarItemDto): string {
  if (item.kind === 'TASK') return 'border-l-slate-300';
  if (item.kind === 'BIRTHDAY') return 'border-l-orange-300';
  if (item.eventType === 'MATCH') return 'border-l-brand-500';
  if (item.eventType === 'TRAINING') return 'border-l-emerald-500';
  return 'border-l-amber-400';
}

/** Geburtstag als Kalendereintrag: ganztaegig, ein Klick oeffnet die Person. */
function birthdayItem(birthday: BirthdayDto): CalendarItemDto {
  const [year, month, day] = birthday.date.split('-').map(Number) as [number, number, number];
  return {
    id: `birthday-${birthday.personId}-${birthday.date}`,
    kind: 'BIRTHDAY',
    title: `🎂 ${birthday.name} (${birthday.age})`,
    startAt: new Date(year, month - 1, day).toISOString(),
    endAt: null,
    allDay: true,
    eventType: 'OTHER',
    location: null,
    organisationUnits: [],
    persons: [{ id: birthday.personId, name: birthday.name }],
    isPersonal: false,
    team: null,
    sourceOfTruth: null,
    syncStatus: null,
    refId: birthday.personId,
  };
}

export function CalendarPage({
  initialTab = 'calendar',
}: {
  initialTab?: 'calendar' | 'matches';
}): JSX.Element {
  const scope = useScope();
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // /spiele/<id> (z. B. aus der Suche): dieses Spiel hervorheben.
  const { id: spielId } = useParams();
  const [tab, setTab] = useState<string>(initialTab);
  const [weeks, setWeeks] = useState(6);
  const [view, setView] = useState('list');
  const [month, setMonth] = useState(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [editingEvent, setEditingEvent] = useState<CalendarEventDto | null>(null);
  const [includeTasks, setIncludeTasks] = useState(false);
  const [showBirthdays, setShowBirthdaysState] = useState(() => {
    try {
      return window.localStorage.getItem(BIRTHDAY_KEY) !== 'aus';
    } catch {
      return true;
    }
  });
  const setShowBirthdays = (value: boolean): void => {
    setShowBirthdaysState(value);
    try {
      window.localStorage.setItem(BIRTHDAY_KEY, value ? 'an' : 'aus');
    } catch {
      // Ohne Speicher gilt die Wahl nur bis zum Neuladen.
    }
  };
  // Geburtstage nur, wer Personen sehen darf - der Server prueft es ohnehin.
  const mayBirthdays = can('persons', 'read');
  // Filter: Mannschaften und Terminart. Leere Auswahl heisst "alles".
  const [teamFilter, setTeamFilter] = useState<Set<string>>(new Set());
  const [artFilter, setArtFilter] = useState<Set<'MATCH' | 'TRAINING' | 'OTHER'>>(new Set());
  const [eventDialog, setEventDialog] = useState(false);
  const [templateFor, setTemplateFor] = useState<MatchDto | null>(null);

  const range = useMemo(() => {
    if (view === 'month') {
      const first = new Date(month.getFullYear(), month.getMonth(), 1);
      first.setDate(first.getDate() - (first.getDay() + 6) % 7);
      const last = new Date(month.getFullYear(), month.getMonth() + 1, 1);
      if (last.getDay() !== 1) last.setDate(last.getDate() + (8 - last.getDay()) % 7);
      return { from: first.toISOString(), to: new Date(last.getTime() - 1).toISOString() };
    }
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    const to = new Date(from.getTime() + weeks * 7 * 86_400_000);
    return { from: from.toISOString(), to: to.toISOString() };
  }, [weeks, view, month]);

  const itemsQuery = {
    ...range,
    ...scopeQuery(scope),
    includeTasks,
  };

  const { data: events, isLoading, error } = useQuery({
    queryKey: ['calendar', itemsQuery],
    queryFn: () => api.get<CalendarItemDto[]>('/api/calendar/items', itemsQuery),
    enabled: tab === 'calendar',
  });
  const birthdayQuery = { ...range, ...scopeQuery(scope) };
  const { data: birthdays } = useQuery({
    queryKey: ['calendar', 'birthdays', birthdayQuery],
    queryFn: () => api.get<BirthdayDto[]>('/api/calendar/birthdays', birthdayQuery),
    enabled: tab === 'calendar' && showBirthdays && mayBirthdays,
  });
  const items = useMemo(() => {
    if (!events) return undefined;
    if (!showBirthdays || !mayBirthdays || !birthdays?.length) return events;
    return [...events, ...birthdays.map(birthdayItem)].sort((a, b) => a.startAt.localeCompare(b.startAt));
  }, [events, birthdays, showBirthdays, mayBirthdays]);

  /** Art eines Eintrags fuer den Filter: Spiel, Training, alles andere. */
  const artVon = (item: CalendarItemDto): 'MATCH' | 'TRAINING' | 'OTHER' =>
    item.eventType === 'MATCH' ? 'MATCH' : item.eventType === 'TRAINING' ? 'TRAINING' : 'OTHER';

  // Auswahl der Mannschaften: was im Zeitraum vorkommt, nicht der ganze Verein.
  const mannschaften = useMemo(() => {
    const namen = new Map<string, string>();
    for (const item of items ?? []) if (item.team) namen.set(item.team.id, item.team.name);
    return [...namen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'de-AT'));
  }, [items]);
  const ohneMannschaft = (items ?? []).some((item) => !item.team && item.kind === 'EVENT');

  const gefiltert = useMemo(() => {
    const passt = (item: CalendarItemDto): boolean => {
      if (artFilter.size > 0 && !artFilter.has(artVon(item))) return false;
      if (teamFilter.size === 0) return true;
      return item.team ? teamFilter.has(item.team.id) : teamFilter.has('OHNE');
    };
    return (items ?? []).filter(passt);
  }, [items, teamFilter, artFilter]);
  const umschalten = <T,>(menge: Set<T>, wert: T): Set<T> => {
    const naechste = new Set(menge);
    if (naechste.has(wert)) naechste.delete(wert); else naechste.add(wert);
    return naechste;
  };

  const matchesQuery = { ...scopeQuery(scope) };
  const { data: matches, isLoading: matchesLoading } = useQuery({
    queryKey: ['matches', matchesQuery],
    queryFn: () => api.get<MatchDto[]>('/api/matches', matchesQuery),
    enabled: tab === 'matches',
  });

  const grouped = useMemo(() => {
    const map = new Map<string, CalendarItemDto[]>();
    for (const item of gefiltert) {
      const key = dayKey(new Date(item.startAt));
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [gefiltert]);

  const openEvent = async (eventId: string): Promise<void> => {
    try {
      const event = await api.get<CalendarEventDto>(`/api/calendar/events/${eventId}`);
      setEditingEvent(event);
      setEventDialog(true);
    } catch (err) { toast.show(err instanceof Error ? err.message : 'Termin nicht verfügbar', 'error'); }
  };

  // /kalender?event=<id> (Suche, Meldungen): Termin öffnen und den Parameter wieder entfernen.
  const eventParam = searchParams.get('event');
  useEffect(() => {
    if (!eventParam) return;
    setTab('calendar');
    void openEvent(eventParam);
    const rest = new URLSearchParams(searchParams);
    rest.delete('event');
    setSearchParams(rest, { replace: true });
  }, [eventParam]);

  const openItem = async (item: CalendarItemDto): Promise<void> => {
    if (item.kind === 'TASK') { navigate(`/aufgaben/${item.refId}`); return; }
    if (item.kind === 'BIRTHDAY') { navigate(`/personen/${item.refId}`); return; }
    await openEvent(item.refId);
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Kalender"
        description="Termine, Trainings und Spiele"
        actions={
          tab === 'calendar' &&
          can('calendar', 'create') && <Button onClick={() => { setEditingEvent(null); setEventDialog(true); }}>+ Termin</Button>
        }
      />

      <Tabs
        tabs={[
          { id: 'calendar', label: 'Termine' },
          { id: 'matches', label: 'Spiele' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'calendar' && (
        <>
          <Card>
            <CardBody className="flex flex-wrap items-center gap-4">
              <Select aria-label="Kalenderansicht" value={view} onChange={(e) => setView(e.target.value)} className="w-auto">
                <option value="list">Liste</option><option value="month">Monat</option>
              </Select>
              {view === 'month' ? <div className="flex items-center gap-2">
                <Button variant="secondary" aria-label="Vorheriger Monat" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}>←</Button>
                <span className="text-sm font-medium">{month.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' })}</span>
                <Button variant="secondary" aria-label="Nächster Monat" onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}>→</Button>
              </div> :
              <Select
                value={String(weeks)}
                onChange={(e) => setWeeks(Number(e.target.value))}
                className="w-auto"
              >
                <option value="2">2 Wochen</option>
                <option value="6">6 Wochen</option>
                <option value="12">3 Monate</option>
                <option value="26">6 Monate</option>
              </Select>}
              <Checkbox
                label="Aufgaben-Deadlines anzeigen"
                checked={includeTasks}
                onChange={setIncludeTasks}
              />
              {mayBirthdays && (
                <Checkbox label="Geburtstage anzeigen" checked={showBirthdays} onChange={setShowBirthdays} />
              )}
              <a className="text-sm font-medium text-brand-700 underline" href={`/api/calendar/export.ics${buildQuery(itemsQuery)}`} download="vereinskalender.ics">ICS exportieren</a>
            </CardBody>
          </Card>

          {(mannschaften.length > 0 || (items?.length ?? 0) > 0) && (
            <Card>
              <CardBody className="space-y-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <span className="text-sm font-medium text-slate-700">Art</span>
                  <Checkbox label="Spiele" checked={artFilter.has('MATCH')} onChange={() => setArtFilter((v) => umschalten(v, 'MATCH'))} />
                  <Checkbox label="Trainings" checked={artFilter.has('TRAINING')} onChange={() => setArtFilter((v) => umschalten(v, 'TRAINING'))} />
                  <Checkbox label="Sonstige" checked={artFilter.has('OTHER')} onChange={() => setArtFilter((v) => umschalten(v, 'OTHER'))} />
                </div>
                {mannschaften.length > 0 && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 pt-2">
                    <span className="text-sm font-medium text-slate-700">Mannschaft</span>
                    {mannschaften.map((team) => (
                      <Checkbox
                        key={team.id}
                        label={team.name}
                        checked={teamFilter.has(team.id)}
                        onChange={() => setTeamFilter((v) => umschalten(v, team.id))}
                      />
                    ))}
                    {ohneMannschaft && (
                      <Checkbox
                        label="ohne Mannschaft"
                        checked={teamFilter.has('OHNE')}
                        onChange={() => setTeamFilter((v) => umschalten(v, 'OHNE'))}
                      />
                    )}
                  </div>
                )}
                <p className="hint">
                  {teamFilter.size === 0 && artFilter.size === 0
                    ? 'Ohne Auswahl wird alles gezeigt.'
                    : `${gefiltert.length} von ${items?.length ?? 0} Terminen`}
                  {(teamFilter.size > 0 || artFilter.size > 0) && (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="font-medium text-brand-700 hover:underline"
                        onClick={() => { setTeamFilter(new Set()); setArtFilter(new Set()); }}
                      >
                        Filter zurücksetzen
                      </button>
                    </>
                  )}
                </p>
              </CardBody>
            </Card>
          )}

          {isLoading && <LoadingBlock />}
          {error && <ErrorBlock error={error} />}
          {items && items.length === 0 && (
            <EmptyState
              title="Keine Termine im gewählten Zeitraum"
              description="Wechsle den Bereich oder erweitere den Zeitraum."
            />
          )}
          {items && items.length > 0 && gefiltert.length === 0 && (
            <EmptyState
              title="Keine Termine mit diesem Filter"
              description="Mit weniger Häkchen wird mehr gezeigt."
            />
          )}

          {view === 'month' ? <CalendarMonth month={month} items={gefiltert} onOpen={(item) => { void openItem(item); }} /> : <div className="space-y-4">
            {grouped.map(([day, dayItems]) => (
              <div key={day}>
                <h2 className="mb-1.5 text-sm font-semibold text-slate-500">
                  {tagUeberschrift(dayItems[0]?.startAt ?? day)}
                </h2>
                <ul className="space-y-2">
                  {dayItems.map((item) => (
                    <li key={item.id}>
                      <Card className={cn('border-l-4', streifen(item))}>
                        <div className="flex items-start gap-3 p-3">
                          <div className="w-12 shrink-0 text-sm font-semibold text-slate-500">
                            {item.allDay ? 'ganzt.' : formatTime(item.startAt)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <button type="button" onClick={() => { void openItem(item); }} className="max-w-full truncate text-left font-medium text-slate-900 hover:text-brand-700">{item.title}</button>
                            <p className="truncate text-xs text-slate-500">
                              {item.kind === 'BIRTHDAY'
                                ? 'Geburtstag'
                                : item.team
                                ? item.team.name
                                : item.isPersonal
                                ? item.persons.map((person) => person.name).join(', ')
                                : item.organisationUnits.map((u) => u.name).join(', ')}
                              {item.location ? ` · ${item.location}` : ''}
                            </p>
                          </div>
                          <div className="flex shrink-0 flex-col items-end gap-1">
                            {item.kind === 'BIRTHDAY' ? (
                              <Badge tone="orange">Geburtstag</Badge>
                            ) : (
                              <Badge tone={EVENT_TONES[item.eventType] ?? 'neutral'}>
                                {EVENT_TYPE_LABELS[item.eventType] ?? item.eventType}
                              </Badge>
                            )}
                            {item.isPersonal && (
                              <Badge tone="neutral" title="Nur für die eingetragenen Personen sichtbar">
                                🔒 Persönlich
                              </Badge>
                            )}
                            {item.sourceOfTruth === 'FOOTBALL_FEDERATION' && (
                              <Badge tone="brand" title="Vom Verband synchronisiert">
                                🔄 Verband
                              </Badge>
                            )}
                            {item.sourceOfTruth === 'GOOGLE' && (
                              <Badge tone="info" title="Aus dem Google-Kalender übernommen">
                                🔄 Google
                              </Badge>
                            )}
                          </div>
                        </div>
                      </Card>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>}
        </>
      )}

      {tab === 'matches' && (
        <MatchesTab
          matches={matches ?? []}
          loading={matchesLoading}
          highlightId={spielId ?? null}
          onCreateWorkflow={(match) => setTemplateFor(match)}
        />
      )}

      {eventDialog && <EventDialog open={eventDialog} existing={editingEvent} onClose={() => setEventDialog(false)} />}
      <TemplateDialog
        open={templateFor !== null}
        onClose={() => setTemplateFor(null)}
        {...(templateFor
          ? {
              defaultUnitIds: [templateFor.organisationUnitId],
              defaultReferenceDate: templateFor.startAt,
            }
          : {})}
      />
    </div>
  );
}

/* --------------------------------------------------------------------- Spiele */

function MatchesTab({
  matches,
  loading,
  highlightId,
  onCreateWorkflow,
}: {
  matches: MatchDto[];
  loading: boolean;
  highlightId: string | null;
  onCreateWorkflow: (match: MatchDto) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();
  // Leere Auswahl heisst "alle Mannschaften".
  const [teamFilter, setTeamFilter] = useState<Set<string>>(new Set());

  // Ziel aus der Suche: hinscrollen; fehlt das Spiel in der Auswahl, ehrlich sagen.
  useEffect(() => {
    if (!highlightId || loading) return;
    const el = document.getElementById(`spiel-${highlightId}`);
    if (el) el.scrollIntoView({ block: 'center' });
    else if (matches.length > 0) toast.show('Das Spiel ist im gewählten Bereich nicht dabei – wechsle den Bereich oben.', 'error');
  }, [highlightId, loading, matches.length]);

  const mannschaften = useMemo(() => {
    const namen = new Map<string, string>();
    for (const match of matches) if (match.teamId) namen.set(match.teamId, match.teamName ?? 'Mannschaft');
    return [...namen.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name, 'de-AT'));
  }, [matches]);
  const ohneMannschaft = matches.some((match) => !match.teamId);
  const gefiltert = useMemo(
    () => matches.filter((match) => {
      if (teamFilter.size === 0) return true;
      return match.teamId ? teamFilter.has(match.teamId) : teamFilter.has('OHNE');
    }),
    [matches, teamFilter],
  );
  const umschalten = (wert: string): void =>
    setTeamFilter((vorher) => {
      const naechste = new Set(vorher);
      if (naechste.has(wert)) naechste.delete(wert); else naechste.add(wert);
      return naechste;
    });

  const sync = useMutation({
    mutationFn: () => api.post<Record<string, number>>('/api/football/sync', {}),
    onSuccess: async (stats) => {
      await queryClient.invalidateQueries({ queryKey: ['matches'] });
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      toast.show(
        `Abgeglichen: ${stats['created'] ?? 0} neu, ${stats['updated'] ?? 0} aktualisiert, ${stats['unchanged'] ?? 0} unverändert`,
      );
    },
    onError: (err: unknown) =>
      toast.show(err instanceof ApiError ? err.message : 'Abgleich fehlgeschlagen', 'error'),
  });

  if (loading) return <LoadingBlock />;

  return (
    <div className="space-y-3">
      {can('matches', 'manage') && (
        <div className="flex justify-end">
          <Button variant="secondary" onClick={() => sync.mutate()} loading={sync.isPending}>
            Mit Verband abgleichen
          </Button>
        </div>
      )}

      {(mannschaften.length > 0 || ohneMannschaft) && matches.length > 0 && (
        <Card>
          <CardBody className="space-y-2">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <span className="text-sm font-medium text-slate-700">Mannschaft</span>
              {mannschaften.map((team) => (
                <Checkbox key={team.id} label={team.name} checked={teamFilter.has(team.id)} onChange={() => umschalten(team.id)} />
              ))}
              {ohneMannschaft && (
                <Checkbox label="ohne Mannschaft" checked={teamFilter.has('OHNE')} onChange={() => umschalten('OHNE')} />
              )}
            </div>
            <p className="hint">
              {teamFilter.size === 0 ? 'Ohne Auswahl werden alle gezeigt.' : `${gefiltert.length} von ${matches.length} Spielen`}
              {teamFilter.size > 0 && (
                <>
                  {' · '}
                  <button type="button" className="font-medium text-brand-700 hover:underline" onClick={() => setTeamFilter(new Set())}>
                    Filter zurücksetzen
                  </button>
                </>
              )}
            </p>
          </CardBody>
        </Card>
      )}

      {matches.length === 0 ? (
        <EmptyState
          title="Keine Spiele vorhanden"
          description="Spiele werden über die Verbandsanbindung oder den manuellen Import eingespielt."
        />
      ) : gefiltert.length === 0 ? (
        <EmptyState
          title="Keine Spiele mit diesem Filter"
          description="Mit weniger Häkchen wird mehr gezeigt."
        />
      ) : (
        <ul className="space-y-2">
          {gefiltert.map((match) => (
            <li key={match.id} id={`spiel-${match.id}`}>
              <Card className={cn(match.id === highlightId && 'ring-2 ring-brand-500')}>
                <div className="flex flex-wrap items-center gap-3 p-3">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium text-slate-900">
                      {match.homeTeam} – {match.awayTeam}
                      {match.homeScore !== null && match.awayScore !== null && (
                        <span className="ml-2 font-semibold text-brand-700">
                          {match.homeScore}:{match.awayScore}
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-slate-500">
                      {formatShort(match.startAt)}
                      {match.location ? ` · ${match.location}` : ''}
                      {match.competition ? ` · ${match.competition}` : ''}
                    </p>
                    <p className="truncate text-xs text-slate-400">
                      {match.teamName ?? match.organisationUnitName}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tone={match.isHome ? 'success' : 'neutral'}>
                      {match.isHome ? 'Heim' : 'Auswärts'}
                    </Badge>
                    <Badge tone={match.status === 'PLAYED' ? 'neutral' : 'info'}>
                      {MATCH_STATUS_LABELS[match.status] ?? match.status}
                    </Badge>
                    {match.isExternal && (
                      <Badge tone="brand" title="Wird vom Verband gepflegt">
                        🔄 Verband
                      </Badge>
                    )}
                    {match.googleCalendarEventId && (
                      <Badge tone="info" title="Im Google-Kalender eingetragen">
                        🔄 Google
                      </Badge>
                    )}
                    {match.isHome && can('tasks', 'create') && (
                      <Button variant="ghost" size="sm" onClick={() => onCreateWorkflow(match)}>
                        Ablauf erzeugen
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------- Termin */

type EventTaskDraft = CalendarEventTaskDto & { assigneePersonIds: string[] };

function EventDialog({ open, onClose, existing }: { open: boolean; onClose: () => void; existing: CalendarEventDto | null }): JSX.Element {
  const scope = useScope();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can, user } = useAuth();
  const readonly = Boolean(existing && (existing.readOnly || !can('calendar', 'update')));

  const [title, setTitle] = useState(existing?.title ?? '');
  const [description, setDescription] = useState(existing?.description ?? '');
  const [eventType, setEventType] = useState<string>(existing?.eventType ?? 'MEETING');
  const [startAt, setStartAt] = useState(existing ? toDateTimeLocal(existing.startAt) : '');
  const [endAt, setEndAt] = useState(existing?.endAt ? toDateTimeLocal(existing.endAt) : '');
  const [allDay, setAllDay] = useState(existing?.allDay ?? false);
  const [location, setLocation] = useState(existing?.location ?? '');
  const [unitIds, setUnitIds] = useState<string[]>(existing?.organisationUnits.map((unit) => unit.id) ?? (scope.unitId ? [scope.unitId] : []));
  const [personIds, setPersonIds] = useState<string[]>(existing?.persons.map((person) => person.id) ?? []);
  const [tasks, setTasks] = useState<EventTaskDraft[]>([]);
  const [templateId, setTemplateId] = useState<string>('');
  const [dialogTab, setDialogTab] = useState<'details' | 'participants' | 'tasks'>('details');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [advanced, setAdvanced] = useState(false);
  useEffect(() => { setErrors({}); }, [title, description, eventType, startAt, endAt, allDay, location, unitIds, personIds, tasks]);
  const taskEditable = !readonly && can('tasks', 'create') && unitIds.length > 0;

  // Ohne Bereich ist der Termin persoenlich: nur die eingetragenen Personen
  // sehen ihn, und nur die anlegende Person kann ihn aendern.
  const persoenlich = unitIds.length === 0;
  const selbstEingetragen = user ? personIds.includes(user.personId) : false;
  const missingHint = !title.trim() ? 'Bitte unter Termin einen Titel eingeben.'
    : !startAt ? 'Bitte unter Termin einen Beginn auswählen.'
    : endAt && endAt < startAt ? 'Das Ende darf nicht vor dem Beginn liegen.'
    : persoenlich && tasks.length > 0 ? 'Bitte unter Teilnehmer einen Bereich für die Aufgaben auswählen.'
    : persoenlich && !selbstEingetragen ? 'Bitte unter Teilnehmer einen Bereich auswählen oder „Nur für mich“ wählen.'
    : tasks.some((task) => !task.title.trim()) ? 'Bitte unter Aufgaben jede Aufgabe benennen.' : '';

  const { data: templates } = useQuery({
    queryKey: ['task-templates'],
    queryFn: () => api.get<TaskTemplateDto[]>('/api/task-templates'),
    enabled: open && taskEditable,
  });

  useEffect(() => {
    if (!open) return;
    setTasks((existing?.tasks ?? []).map((task) => ({ ...task, assigneePersonIds: task.assignees.map((a) => a.id) })));
    setTemplateId('');
    setDialogTab('details');
    setErrors({});
  }, [open, existing]);

  const addTemplate = (id: string): void => {
    setTemplateId(id);
    const template = templates?.find((t) => t.id === id);
    if (!template) return;
    const reference = Date.parse(fromDateTimeLocal(startAt) ?? '');
    setTasks((current) => [...current, ...template.items.map((item) => ({
      id: `new-${item.id}-${crypto.randomUUID()}`,
      title: item.title,
      dueAt: Number.isNaN(reference) ? null : new Date(reference + item.offsetDays * 86_400_000).toISOString(),
      assignees: [],
      assigneePersonIds: [],
    }))]);
  };

  const nurFuerMich = (): void => {
    if (!user) return;
    setUnitIds([]);
    setPersonIds([user.personId]);
  };

  const save = useMutation({
    mutationFn: async () => {
      const event = await (existing ? api.patch<CalendarEventDto> : api.post<CalendarEventDto>)(existing ? `/api/calendar/events/${existing.id}` : '/api/calendar/events', {
        title,
        description: description || null,
        eventType,
        startAt: fromDateTimeLocal(startAt),
        endAt: fromDateTimeLocal(endAt),
        allDay,
        location: location || null,
        organisationUnitIds: unitIds,
        personIds,
      });
      if (taskEditable && (tasks.length > 0 || existing)) {
        await api.put(`/api/calendar/events/${event.id}/tasks`, {
          templateId: templateId || null,
          tasks: tasks.map((task) => ({
            ...(task.id.startsWith('new-') ? {} : { id: task.id }),
            title: task.title,
            dueAt: task.dueAt,
            assigneePersonIds: task.assigneePersonIds,
          })),
        });
      }
      return event;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.show(existing ? 'Termin gespeichert' : 'Termin angelegt');
      setTitle('');
      setDescription('');
      setStartAt('');
      setEndAt('');
      setLocation('');
      onClose();
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const fields = error.fieldErrors();
        setErrors(fields);
        if (fields.organisationUnitIds || fields.personIds) setDialogTab('participants');
        else if (Object.keys(fields).some((key) => key.startsWith('tasks'))) setDialogTab('tasks');
        else { setDialogTab('details'); setAdvanced(true); }
        toast.show(error.message, 'error');
      } else toast.show('Der Termin konnte nicht gespeichert werden. Bitte versuche es erneut.', 'error');
    },
  });

  const dirty = title !== (existing?.title ?? '') || description !== (existing?.description ?? '')
    || eventType !== (existing?.eventType ?? 'MEETING') || location !== (existing?.location ?? '')
    || startAt !== (existing ? toDateTimeLocal(existing.startAt) : '')
    || endAt !== (existing?.endAt ? toDateTimeLocal(existing.endAt) : '') || allDay !== (existing?.allDay ?? false)
    || JSON.stringify(unitIds) !== JSON.stringify(existing?.organisationUnits.map((unit) => unit.id) ?? (scope.unitId ? [scope.unitId] : []))
    || JSON.stringify(personIds) !== JSON.stringify(existing?.persons.map((person) => person.id) ?? [])
    || JSON.stringify(tasks) !== JSON.stringify((existing?.tasks ?? []).map((task) => ({ ...task, assigneePersonIds: task.assignees.map((a) => a.id) })));
  const requestClose = useUnsavedChanges(open, !readonly && dirty, onClose, save.isPending);

  // Trainings und Spiele gehören zur Mannschaft; Verbandstermine sind schreibgeschützt.
  const loeschbar = Boolean(existing && !readonly && !existing.readOnly && !existing.sports
    && (existing.isPersonal ? existing.createdByPersonId === user?.personId : can('calendar', 'delete')));
  const remove = useMutation({
    mutationFn: () => api.del(`/api/calendar/events/${existing?.id}`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['calendar'] });
      await queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.show('Termin gelöscht');
      onClose();
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Der Termin konnte nicht gelöscht werden.', 'error'),
  });
  const submit = (): void => {
    if (missingHint) {
      const fields: Record<string, string> = {};
      if (!title.trim()) fields.title = 'Bitte gib einen Titel ein.';
      if (!startAt) fields.startAt = 'Bitte wähle einen Beginn aus.';
      if (endAt && endAt < startAt) fields.endAt = 'Das Ende darf nicht vor dem Beginn liegen.';
      if (Object.keys(fields).length) setDialogTab('details');
      else if (persoenlich && (!selbstEingetragen || tasks.length > 0)) {
        fields.organisationUnitIds = tasks.length > 0 ? 'Für Aufgaben bitte mindestens einen Bereich auswählen.' : 'Wähle „Nur für mich“ oder einen Bereich aus.';
        setDialogTab('participants');
      } else { fields.tasks = 'Bitte benenne jede Aufgabe.'; setDialogTab('tasks'); }
      setErrors(fields);
      return;
    }
    setErrors({});
    save.mutate();
  };

  return (
    <Dialog
      open={open}
      onClose={requestClose}
      title={existing ? (readonly ? 'Termin ansehen' : 'Termin bearbeiten') : 'Neuer Termin'}
      wide
      footer={
        <div className="w-full space-y-2">
          {!readonly && missingHint && <p className="text-sm text-slate-600" aria-live="polite">{missingHint}</p>}
          <div className="flex flex-wrap justify-end gap-2">
          {loeschbar && <Button
            variant="danger"
            className="mr-auto"
            loading={remove.isPending}
            disabled={save.isPending}
            onClick={() => {
              if (window.confirm(`Termin "${existing?.title ?? ''}" wirklich löschen?`)) remove.mutate();
            }}
          >
            Termin löschen
          </Button>}
          <Button variant="secondary" onClick={requestClose} disabled={save.isPending || remove.isPending}>
            Abbrechen
          </Button>
          {!readonly && <Button
            onClick={submit}
            loading={save.isPending}
          >
            Termin speichern
          </Button>}
          </div>
        </div>
      }
    >
      <div className="mb-4 flex gap-1 rounded-xl bg-slate-100 p-1" role="tablist" aria-label="Terminbereiche">
        {([
          ['details', 'Termin'],
          ['participants', 'Teilnehmer'],
          ['tasks', `Aufgaben${tasks.length > 0 ? ` (${tasks.length})` : ''}`],
        ] as const).filter(([id]) => id !== 'tasks' || can('tasks', 'create') || (can('tasks', 'read') && tasks.length > 0)).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={dialogTab === id}
            onClick={() => setDialogTab(id)}
            className={cn(
              'flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
              dialogTab === id ? 'bg-white text-brand-700 shadow-sm' : 'text-slate-500 hover:text-slate-700',
            )}
          >
            {label}
          </button>
        ))}
      </div>
      {/* Sporttermin: von hier ohne Umweg zu Zu- und Absagen. */}
      {existing?.sports && can('sports_participation', 'read') && (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm">
          <span className="text-brand-900">
            {existing.sports.kind === 'MATCH' ? 'Spiel' : existing.sports.kind === 'WORK' ? 'Arbeitseinsatz' : 'Training'}
            {existing.sports.teamName ? ` der ${existing.sports.teamName}` : ''}
          </span>
          <Link
            to={`/sport/${existing.sports.teamId}?reiter=termine&anwesenheit=${existing.sports.sessionId}`}
            onClick={onClose}
            className="font-medium text-brand-700 hover:underline"
          >
            Rückmeldungen und Anwesenheit
          </Link>
        </div>
      )}
      {existing?.readOnly && <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">Dieser Termin kommt vom Verband und kann hier nicht geändert werden.</p>}
      {existing?.sports && !existing.readOnly && !readonly && <p className="mb-4 rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">Trainings und Spiele löschst du in der Mannschaft.</p>}
      {Object.keys(errors).length > 0 && <p role="alert" className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-800">Bitte prüfe die markierten Angaben. Deine Eingaben bleiben erhalten.</p>}
      <fieldset disabled={readonly || save.isPending} className="space-y-4">
        {dialogTab === 'details' && <>
        <Field label="Titel" required error={errors['title']}>
          {(id) => <Input id={id} value={title} onChange={(e) => setTitle(e.target.value)} />}
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Beginn" required error={errors['startAt']}>
            {(id) => (
              <Input
                id={id}
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
              />
            )}
          </Field>
          <Field label="Ende" error={errors['endAt']}>
            {(id) => (
              <Input
                id={id}
                type="datetime-local"
                value={endAt}
                onChange={(e) => setEndAt(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field label="Ort">
          {(id) => <Input id={id} value={location} onChange={(e) => setLocation(e.target.value)} placeholder="z. B. Vereinsheim" />}
        </Field>
        <button type="button" aria-expanded={advanced} onClick={() => setAdvanced((value) => !value)} className="min-h-11 text-sm font-medium text-brand-700 hover:underline">
          {advanced ? 'Weitere Angaben ausblenden' : 'Weitere Angaben'}
        </button>
        {advanced && <div className="space-y-4 rounded-lg bg-slate-50 p-3">
          <Field label="Art" error={errors['eventType']}>
            {(id) => (
              <Select id={id} value={eventType} onChange={(e) => setEventType(e.target.value)}>
                {Object.entries(EVENT_TYPE_LABELS)
                  .filter(([value]) => value !== 'TASK_DEADLINE')
                  .map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
              </Select>
            )}
          </Field>
        <Checkbox label="Ganztägig" checked={allDay} onChange={setAllDay} />
        <Field label="Notizen" error={errors['description']}>
          {(id) => <Textarea id={id} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />}
        </Field>
        </div>}
        </>}
        {dialogTab === 'participants' && <>
        <Field
          label="Bereiche"
          hint="Leer lassen für einen persönlichen Termin"
          error={errors['organisationUnitIds']}
        >
          {() => (
            <>
              <OrgUnitPicker value={unitIds} onChange={setUnitIds} />
              {user && (
                <button
                  type="button"
                  onClick={nurFuerMich}
                  className="mt-2 min-h-11 text-sm font-medium text-brand-700 underline-offset-2 hover:underline"
                >
                  Nur für mich
                </button>
              )}
            </>
          )}
        </Field>

        <Field
          label="Personen"
          hint={
            persoenlich
              ? 'Nur diese Personen sehen den Termin'
              : 'Zusätzlich eingeladen – sie sehen den Termin auch ohne Bereichsrecht'
          }
          error={errors['personIds']}
        >
          {(id) => (
            // Die eigene Person steht oben und traegt ein "(ich)" - sonst waere
            // nach "Nur fuer mich" nicht zu sehen, dass man selbst drin steht.
            <PersonPicker
              id={id}
              value={personIds}
              onChange={setPersonIds}
              preferId={user?.personId}
              decorate={(person) =>
                person.id === user?.personId ? `${person.displayName} (ich)` : person.displayName
              }
            />
          )}
        </Field>

        {!persoenlich && <p className="rounded-lg bg-brand-50 p-3 text-sm text-brand-800">Diesen Termin sehen Personen mit Leserecht für die ausgewählten Bereiche und die zusätzlich eingetragenen Personen.</p>}
        {persoenlich && (
          <p
            className={cn(
              'rounded-lg px-3 py-2 text-sm',
              selbstEingetragen
                ? 'bg-brand-50 text-brand-800'
                : 'bg-amber-50 text-amber-800',
            )}
          >
            {selbstEingetragen
              ? 'Persönlicher Termin: sichtbar nur für die oben gewählten Personen, änderbar nur durch dich.'
              : 'Ohne Bereich brauchst du dich selbst in der Personenliste – sonst lässt sich der Termin nicht speichern.'}
          </p>
        )}
        </>}
        {dialogTab === 'tasks' && <>
        <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Aufgaben zum Termin</h3>
              <p className="text-sm text-slate-500">Was ist zu tun, wer übernimmt es und bis wann?</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {taskEditable && templates && templates.length > 0 && (
                <Select aria-label="Aufgabenvorlage" value={templateId} onChange={(e) => addTemplate(e.target.value)} className="w-auto text-sm">
                  <option value="">Vorlage verwenden …</option>
                  {templates.map((template) => <option key={template.id} value={template.id}>{template.name}</option>)}
                </Select>
              )}
              {taskEditable && <Button type="button" variant="secondary" size="sm" onClick={() => setTasks((current) => [...current, { id: `new-${Date.now()}`, title: '', dueAt: null, assignees: [], assigneePersonIds: [] }])} disabled={persoenlich}>
                Einzelne Aufgabe hinzufügen
              </Button>}
            </div>
          </div>
          {persoenlich && <p className="text-xs text-amber-700">Für Aufgaben bitte mindestens einen Bereich auswählen.</p>}
          {errors['tasks'] && <p role="alert" className="text-sm text-rose-700">{errors['tasks']}</p>}
          {tasks.length > 0 && <div className="space-y-2">
            {tasks.map((task, index) => <div key={task.id} className="grid gap-2 rounded-lg bg-white p-2 sm:grid-cols-[1.4fr_1fr_10rem_auto] sm:items-center">
              <Field label={`Aufgabe ${index + 1}`} required>{(id) => <Input id={id} disabled={!taskEditable} value={task.title} placeholder="Was ist zu tun?" onChange={(e) => setTasks((current) => current.map((entry) => entry.id === task.id ? { ...entry, title: e.target.value } : entry))} />}</Field>
              <div><span className="mb-1 block text-sm font-medium text-slate-700">Wer?</span><PersonSelect disabled={!taskEditable} ariaLabel={`Wer erledigt Aufgabe ${index + 1}?`} value={task.assigneePersonIds[0] ?? null} onChange={(id) => setTasks((current) => current.map((entry) => entry.id === task.id ? { ...entry, assigneePersonIds: id ? [id] : [] } : entry))} /></div>
              <Field label="Bis wann?">{(id) => <Input id={id} disabled={!taskEditable} type="date" value={toDateInput(task.dueAt)} onChange={(e) => setTasks((current) => current.map((entry) => entry.id === task.id ? { ...entry, dueAt: fromDateInput(e.target.value, true) } : entry))} />}</Field>
              {taskEditable && <Button type="button" variant="ghost" size="sm" aria-label="Aufgabe entfernen" onClick={() => setTasks((current) => current.filter((entry) => entry.id !== task.id))}>Entfernen</Button>}
            </div>)}
          </div>}
        </div>
        </>}
      </fieldset>
    </Dialog>
  );
}
